/**
 * Office Finder component
 *
 * @module components/office-finder
 */

import Component from './component';
import GMapApi from '../libs/gmap-api';
import AbortableAsync from '../libs/abortable-async';
import AutoComplete from './auto-complete';
import utils from '../utils';
import { objectHasOwnProperty } from '../utils/functions';

const getModule = utils.modules.load;

const EVENT_NAMESPACE = '.aggrekoOfficeFinder';

// default options
const DEF_OPTIONS = {
  'maxResults': 4
};


export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';
  /**
   * OfficeFinder component class
   */
  class OfficeFinder extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, $.extend(true, {}, DEF_OPTIONS, options));
      this.name = 'office-finder';
    }

    /**
     * Main initializer
     * 
     * @returns void
     */
    init() {
      const el = this.element;

      if (el.hasClass(this.initializedClass)) {
        // only need to initialize this component once
        return;
      }

      // load API if it's not loaded yet
      if (!OfficeFinder.apiReady) {
        OfficeFinder.loadApi()
          .then(() => {
            this.init();
          })
          .catch(err => {
            this.logError(err);
          });
        return;
      }

      // mark as initialized
      el.addClass(this.initializedClass);
      
      // parse options from element's `data-*` attributes
      this.parseOptions();

      // init search form functionality
      this.initSearchForm();

      // init auto complete
      this.initAutoComplete();

      // init templates
      this.initTemplates();
    }

    /**
     * Parse options from element's `data-*` attributes.
     */
    parseOptions() {
      const data = this.element.data();
      for (var k in this.options) {
        if (objectHasOwnProperty(this.options, k) && data[k]) {
          this.options[k] = data[k];
        }
      }
    }

    /**
     * Hide specific element.
     * 
     * @param {jQuery} el 
     * @param {String} [showClass]
     * @param {String} [hideClass]
     */
    hideElement(el, showClass, hideClass) {
      el.hide().attr('aria-hidden', 'true').removeClass(showClass || '').addClass(hideClass || '');
    }

    /**
     * Show specific element.
     * 
     * @param {jQuery} el 
     * @param {String} [showClass]
     * @param {String} [hideClass]
     */
    showElement(el, showClass, hideClass) {
      el.show().attr('aria-hidden', 'false').removeClass(hideClass || '').addClass(showClass || '');
    }

    /**
     * Init handlebars templates
     * 
     */
    initTemplates() {
      this.tpls = {};

      // search result
      try {
        this.tpls.searchResult = this.element.find(OfficeFinder.selectors.searchResultTemplate);
        if (this.tpls.searchResult.length) {
          this.tpls.searchResult = OfficeFinder.handlebars.compile(this.tpls.searchResult.first().html());
        }
        else {
          return;
        }
      }
      catch (err) {
        this.logError(err);
        return;
      }
    }

    /**
     * Initialize Search Form functionality.
     */
    initSearchForm() {
      // if search form doesn't exist, no point to continue.
      this.searchForm = $(OfficeFinder.selectors.searchForm, this.element);
      if (!this.searchForm.length) {
        this.logWarning('Search Form not exists.');
        return;
      }

      this.searchResultContainer = this.element.find(OfficeFinder.selectors.searchResultContainer);

      this.searchForm.off(EVENT_NAMESPACE);
      this.searchForm.on(`submit${EVENT_NAMESPACE}`, this._onSearchFormSubmit.bind(this));
    }

    /**
     * Initialize Auto Complete functionality.
     */
    initAutoComplete() {
      try {
        this.autoCompleteService = new OfficeFinder.mapApi.places.AutocompleteService();

        // dummy DIV for PlacesService API
        this.elPlacesService = document.createElement('div');
        this.placesService = new OfficeFinder.mapApi.places.PlacesService(this.elPlacesService);
      } catch (err) {
        this.logError(err);
        return;
      }

      this.autoComplete = new AutoComplete(this.element, {
        selectors: {
          autoCompleteContainer: OfficeFinder.selectors.autoCompleteContainer,
          autoCompleteListContainer: OfficeFinder.selectors.autoCompleteListContainer,
          autoCompleteItem: OfficeFinder.selectors.autoCompleteItem,
          autoCompleteListTemplate: OfficeFinder.selectors.autoCompleteListTemplate,
          searchInput: OfficeFinder.selectors.searchFormAddressInput
        },
        onRequestAutoComplete: this._onRequestAutoComplete.bind(this)
      });

      this.element.on(`autocomplete-itemselect${EVENT_NAMESPACE}`, this._onAutoCompleteItemSelect.bind(this));

      if (!$(OfficeFinder.selectors.mapComponent).length) {
        $(this.autoComplete.autoCompleteContainer, this.element).addClass('-show-google-logo');
      }
    }

    /**
     * Update Gmap component
     * 
     * @param {Object} data 
     */
    updateMap(data) {
      if (data && data.Results && data.Results.length) {
        const coords = data.Results.map(r => { return { lat: r.Latitude, lng: r.Longitude }; });

        // Prevents max zoom with a single office
        if (coords.length == 1) {
          let newCoords = { lat: coords[0].lat + 0.1, lng: coords[0].lng };
          coords.push(newCoords);
        }

        $(OfficeFinder.selectors.mapComponent).each((idx, el) => {
          try {
            const gmapInst = utils.component.getComponent(el, 'gmap');
            if (gmapInst) {
              gmapInst.showArea(coords);
            }
          }
          catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
          }
        });
      }
    }

    /**
     * Search Form submit handler.
     * 
     * @param {Event} ev 
     */
    _onSearchFormSubmit(ev) {
      ev.preventDefault();

      this.autoComplete.select();
    }

    /**
     * Make request to backend API.
     * 
     * @param {Object} prediction 
     */
    _submit(prediction) {
      if (!prediction) {
        return;
      }

      if (this.getPlaceDetailsRequest) {
        if (this.getPlaceDetailsRequest.submitXhr) {
          this.getPlaceDetailsRequest.submitXhr.abort();
        }

        this.getPlaceDetailsRequest.abort();
        this.getPlaceDetailsRequest = null;
      }

      const req = new AbortableAsync();
      this.getPlaceDetailsRequest = req;

      req
        .run(() => {
          this.placesService.getDetails(
            {
              placeId: prediction.place_id
            },
            (details, status) => {
              if (status !== OfficeFinder.mapApi.places.PlacesServiceStatus.OK) {
                return;
              }

              this.searchForm.find(OfficeFinder.selectors.searchFormLatInput).val(details.geometry.location.lat());
              this.searchForm.find(OfficeFinder.selectors.searchFormLngInput).val(details.geometry.location.lng());

              // request JSONP
              req.submitXhr = $.ajax({
                url: this.searchForm.prop('action'),
                data: this.searchForm.serialize(),
                type: 'GET',
                dataType: 'jsonp',
                success: (data, textStatus, jqXHR) => {
                  req.resolve(data);
                },
                error: (jqXHR, textStatus, errorThrown) => {
                  req.reject(textStatus);
                }
              });
            }
          );
        })
        .then((data) => {
          data.HasResults = (data.Results.length > 0);
          this.searchResultContainer.empty().html(this.tpls.searchResult(data));
          this.showElement(this.searchResultContainer, '-show');

          if ($(OfficeFinder.selectors.mapComponent).length) {
            this.updateMap(data);
          }
        })
        .catch(err => {
          const data = {
            HasResults: false
          };
          this.searchResultContainer.empty().html(this.tpls.searchResult(data));
          this.showElement(this.searchResultContainer, '-show');
        });
    }

    /**
     * Search and feed data to autocomplete component.
     * 
     * @param {String} keyword user input
     * @param {Callback} resolve call with predictions array of auto complete search result.
     * @param {Callback} reject call with error message or object.
     */
    _onRequestAutoComplete(keyword, resolve, reject) {
      this.autoCompleteService.getPlacePredictions(
        {
          input: keyword,
          componentRestrictions: {}
        },
        (predictions, status) => {
          if (status != OfficeFinder.mapApi.places.PlacesServiceStatus.OK) {
            reject('getPlacePredictions error ' + status);
          }
          else {
            resolve(predictions);
          }
        }
      );
    }

    /**
     * Autocomplete item select handler.
     * 
     * @param {Event} ev 
     * @param {Object} prediction 
     */
    _onAutoCompleteItemSelect(ev, prediction) {
      if (prediction) {
        this._submit(prediction);
      }
    }
  }

  OfficeFinder.selectors = {
    searchForm: '.office-finder--search-form form',
    searchFormAddressInput: '.office-finder--search-form .search-field :input[type="search"]',
    searchFormLatInput: ':input.office-finder--input-lat',
    searchFormLngInput: ':input.office-finder--input-lng',
    searchResultContainer: '.office-finder--search-result',
    searchResultTemplate: '[data-office-finder--tpl="search-result"]',
    autoCompleteContainer: '.office-finder--autocomplete',
    autoCompleteListContainer: '.office-finder--autocomplete-items',
    autoCompleteListTemplate: '[data-office-finder--tpl="autocomplete-list"]',
    autoCompleteItem: '.office-finder--autocomplete-item',
    mapComponent: '[data-component="gmap"]'
  };

  // APIs object
  OfficeFinder.mapApi = null;
  OfficeFinder.handlebars = null;

  // API loader
  OfficeFinder.apiReady = false;
  OfficeFinder.loadApi = () => {
    if (OfficeFinder._loadingApi) {
      return OfficeFinder._loadingApi;
    }

    OfficeFinder._loadingApi = new Promise((resolve, reject) => {

      Promise.all([
        GMapApi.loadApi({
          libraries: ['places']
        }),
        getModule('handlebars')
      ])
        .then(([mapApi, handlebars]) => {
          OfficeFinder.apiReady = true;
          OfficeFinder.mapApi = mapApi;
          OfficeFinder.handlebars = handlebars;
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });

    return OfficeFinder._loadingApi;
  };

  return OfficeFinder;
})(jQuery, window);